import { Comment } from "@prisma/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { IComment } from "immigram-ui-cmon/src/components/Comments/types";

export const getCommentsByExternalId = async (
  externalId: string
): Promise<Comment[]> => {
  return await axios
    .get<Comment[]>(`${process.env.NEXT_PUBLIC_API_URL}/comments/${externalId}`)
    .then((res) => res.data);
};

export const useComments = (applicationId: string) =>
  useQuery(["comments"], async () => {
    const items = await getCommentsByExternalId(applicationId);
    return items.map((item) => {
      const { sectionFrom, sectionTo, replyId, ...data } = item;
      const result = {
        ...data,
        replyId: replyId ?? undefined,
      } as IComment;
      if (sectionTo && sectionFrom) {
        result.selection = {
          from: sectionFrom,
          to: sectionTo,
        };
      }
      return result;
    });
  });

export const upsertComment = async (
  externalId: string,
  data: Comment
): Promise<Comment> => {
  return await axios
    .post(`${process.env.NEXT_PUBLIC_API_URL}/comments/${externalId}`, {
      data,
    })
    .then((res) => res.data);
};

export const useUpsertCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Comment,
    null,
    { applicationId: string; data: Comment },
    () => void
  >(({ applicationId, data }) => upsertComment(applicationId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["comments"]);
    },
  });
};

export const publishComments = async (
  externalId: string,
  idList: string[],
  pageUrl?: string,
  title?: string
): Promise<Comment[]> => {
  return await axios
    .post(`${process.env.NEXT_PUBLIC_API_URL}/comments/${externalId}/publish`, {
      idList,
      pageUrl,
      title,
    })
    .then((res) => res.data);
};

export const usePublishCommentsMutation = (applicationId: string) => {
  const queryClient = useQueryClient();

  return useMutation<Comment[], null, Comment[], () => void>(
    (comments: Comment[]) => {
      const idList = comments.map((comment) => comment.id);

      return publishComments(applicationId, idList);
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(["comments"]);
      },
    }
  );
};

export const deleteComment = async (
  externalId: string,
  id: string
): Promise<void> => {
  return await axios
    .delete(`${process.env.NEXT_PUBLIC_API_URL}/comments/${externalId}/${id}`)
    .then((res) => res.data);
};

export const useDeleteCommentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    null,
    { applicationId: string; id: string },
    () => void
  >(({ applicationId, id }) => deleteComment(applicationId, id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["comments"]);
    },
  });
};
